import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateCompetitorResearchSurveyTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const longTextId = uuidv4();
  const longTextBlockId = uuidv4();
  const dropdownId = uuidv4();
  const dropdownBlockId = uuidv4();
  const optionScaleId = uuidv4();
  const optionScaleBlockId = uuidv4();
  const multipleChoiceId = uuidv4();
  const multipleChoiceBlockId = uuidv4();
  const endScreen1Id = uuidv4();
  const endScreen1BlockId = uuidv4();
  const endScreen2Id = uuidv4();
  const endScreen2BlockId = uuidv4();

  // Generate new UUIDs for select options
  const dropdownOption1Id = uuidv4();
  const dropdownOption2Id = uuidv4();
  const dropdownOption3Id = uuidv4();
  const mcOptionYesId = uuidv4();
  const mcOptionNoId = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();

  // Generate new UUIDs for logic items and conditions
  const logicItem1Id = uuidv4();
  const logicItem2Id = uuidv4();
  const condition1Id = uuidv4();
  const condition2Id = uuidv4();

  return {
    ...themes.espressoGoldenSand,
    "collectionId": collectionId,
    "name": "Competitor Research Survey Template",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Help us understand the market a little better",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Start survey",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "A person sitting at a table with a tablet",
          "link": "https://unsplash.com/photos/mn-akTsxIeI/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyNHx8Q2hhcnRzfGVufDB8fHx8MTc2NDQ5MTE3Nnww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "content": "First, what's your full name?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "So we know who we're hearing from.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": true
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": emailId,
        "blockId": emailBlockId,
        "content": `And your email, {{${contactInfoBlockId}__first_name}}?`,
        "manditory": true,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We may follow up if we have additional questions.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": longTextId,
        "blockId": longTextBlockId,
        "content": "When you think about brands that offer products like ours, which ones come to mind?",
        "manditory": true,
        "order": 3,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "List any competitors or alternatives you're familiar with.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "DROPDOWN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": dropdownId,
        "blockId": dropdownBlockId,
        "content": "How do you feel those brands stack up against us?",
        "manditory": true,
        "order": 4,
        "selectOptions": [
          {
            "id": dropdownOption1Id,
            "order": 0,
            "value": "They offer more"
          },
          {
            "id": dropdownOption2Id,
            "order": 1,
            "value": "They offer less"
          },
          {
            "id": dropdownOption3Id,
            "order": 2,
            "value": "They're about the same"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "OPTION_SCALE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": optionScaleId,
        "blockId": optionScaleBlockId,
        "content": "How likely are you to choose us over one of those brands?",
        "manditory": true,
        "order": 5,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 0,
        "maxRange": 10,
        "minLabel": "Not likely at all",
        "maxLabel": "Extremely likely",
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": multipleChoiceId,
        "blockId": multipleChoiceBlockId,
        "content": "Would you be open to a short follow-up chat if we need more insight?",
        "manditory": true,
        "order": 6,
        "selectOptions": [
          {
            "id": mcOptionYesId,
            "order": 0,
            "value": "Yes"
          },
          {
            "id": mcOptionNoId,
            "order": 1,
            "value": "No"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": endScreen2BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoiceId,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition1Id,
                "value": "false",
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoiceBlockId,
                "parentLogicItemId": logicItem1Id
              }
            ]
          },
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem2Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": endScreen1BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoiceId,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition2Id,
                "value": "true",
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoiceBlockId,
                "parentLogicItemId": logicItem2Id
              }
            ]
          }
        ],
        "horizontalAlignment": true,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreen1Id,
        "blockId": endScreen1BlockId,
        "content": "Thanks for your feedback!",
        "manditory": false,
        "order": 7,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll reach out soon with any follow-up questions.",
        "thirdContent": "Visit Website",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreen2Id,
        "blockId": endScreen2BlockId,
        "content": "Thanks for your input!",
        "manditory": false,
        "order": 8,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your insights help us strengthen what we offer.",
        "thirdContent": "Visit Website",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      }
    ],
    "snippets": [],
    "metaTitle": "Competitor Research Survey Template",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateCompetitorResearchSurveyTemplate;